'use client';

import { Search, Users, Globe2, SlidersHorizontal, ChevronDown, X, Star, BarChart2, Zap, Sparkles, ShieldCheck, PenLine, Layers, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useLang } from '@/lib/i18n/LanguageContext';

export type FeedTab = 'all' | 'for_you' | 'following' | 'top_rated' | 'verified' | 'open' | 'closed';
export type FeedFilter = FeedTab;

export type MarketFilter = 'ALL' | 'FOREX' | 'CRYPTO' | 'STOCKS' | 'INDICES' | 'COMMODITIES';
export type TimeframeFilter = 'ALL' | 'M1' | 'M5' | 'M15' | 'M30' | 'H1' | 'H4' | 'D1';
export type DirectionFilter = 'ALL' | 'BUY' | 'SELL';
export type StatusFilter = 'ALL' | 'OPEN' | 'WIN' | 'LOSS' | 'BE';
export type SortOption = 'newest' | 'trending' | 'most_liked';
export type IdeaTypeFilter = 'ALL' | 'SIGNAL' | 'ANALYSIS';

export interface FeedFilters {
  tab: FeedTab;
  ideaType: IdeaTypeFilter;
  market: MarketFilter;
  timeframe: TimeframeFilter;
  direction: DirectionFilter;
  status: StatusFilter;
  sort: SortOption;
  search: string;
}

interface FeedFilterBarProps {
  filters: FeedFilters;
  onChange: (f: Partial<FeedFilters>) => void;
}

const marketOptions: MarketFilter[] = ['ALL', 'FOREX', 'CRYPTO', 'STOCKS', 'INDICES', 'COMMODITIES'];
const timeframeOptions: TimeframeFilter[] = ['ALL', 'M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'];
const directionOptions: DirectionFilter[] = ['ALL', 'BUY', 'SELL'];
const statusOptions: StatusFilter[] = ['ALL', 'OPEN', 'WIN', 'LOSS', 'BE'];
const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'trending', label: 'Trending' },
  { value: 'most_liked', label: 'Most Liked' },
];

// Curated/personalized feed tabs. Since the 2026-07-26 owner swap these live in the LEFT SIDEBAR
// on desktop (FeedLeftSidebar imports this list) and as a compact chip row on mobile. The market
// pill row that used to sit above them was removed on 2026-09-04 (owner): markets are filtered
// from the Filter panel instead, which keeps the mobile feed to one scrolling strip.
export const FEED_TAB_DEFS: { id: FeedTab; label: string; icon: LucideIcon; color: string }[] = [
  { id: 'all',       label: 'All Ideas',         icon: Globe2,      color: '#60a5fa' },
  { id: 'for_you',   label: 'For You',           icon: Sparkles,    color: '#a78bfa' },
  { id: 'following', label: 'Following',         icon: Users,       color: '#60a5fa' },
  { id: 'top_rated', label: 'Top Rated',         icon: Star,        color: '#fbbf24' },
  { id: 'verified',  label: 'Verified',          icon: ShieldCheck, color: '#34d399' },
  { id: 'open',      label: 'Open Trades',       icon: Zap,         color: '#34d399' },
  { id: 'closed',    label: 'Closed Trades',     icon: BarChart2,   color: '#fb923c' },
];

// SIGNAL vs ANALYSIS switch (owner, 2026-09-23). Colors mirror IdeaTypeBadge so the switch and
// the badges on the cards read as the same system: indigo = signal, amber = analysis.
const IDEA_TYPE_DEFS: { id: IdeaTypeFilter; label: string; icon: LucideIcon; color: string; muted: string }[] = [
  { id: 'ALL',      label: 'All',      icon: Layers,  color: 'var(--text-primary)', muted: 'var(--bg-card)' },
  { id: 'SIGNAL',   label: 'Signals',  icon: Zap,     color: 'var(--accent)',       muted: 'var(--accent-muted)' },
  { id: 'ANALYSIS', label: 'Analyses', icon: PenLine, color: 'var(--win)',          muted: 'var(--win-muted)' },
];

function IdeaTypeSwitch({ value, onChange }: { value: IdeaTypeFilter; onChange: (v: IdeaTypeFilter) => void }) {
  const { t } = useLang();
  const index = Math.max(0, IDEA_TYPE_DEFS.findIndex((d) => d.id === value));
  const active = IDEA_TYPE_DEFS[index];
  return (
    <div
      role="tablist"
      aria-label={t('Post type')}
      className="relative grid grid-cols-3 shrink-0"
      style={{ borderRadius: 999, padding: 3, background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}
    >
      {/* Sliding thumb: one element that moves, so switching animates instead of blinking */}
      <span
        aria-hidden
        className="absolute top-[3px] bottom-[3px] left-[3px] transition-all duration-200 ease-out"
        style={{
          width: 'calc((100% - 6px) / 3)',
          transform: `translateX(${index * 100}%)`,
          borderRadius: 999,
          background: active.muted,
          border: `1px solid ${active.id === 'ALL' ? 'var(--border)' : active.color}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }}
      />
      {IDEA_TYPE_DEFS.map((d) => {
        const isActive = d.id === value;
        const Icon = d.icon;
        return (
          <button
            key={d.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(d.id)}
            className="relative z-10 flex items-center justify-center gap-1 transition-colors duration-150"
            style={{ padding: '5px 11px', fontSize: 12, fontWeight: 700, color: isActive ? d.color : 'var(--text-secondary)', whiteSpace: 'nowrap' }}
          >
            <Icon size={12} />
            {t(d.label)}
          </button>
        );
      })}
    </div>
  );
}

// tone maps a semantic direction/status onto the theme's paired color+muted-background tokens
// (var(--bullish)/var(--bullish-muted) etc.) instead of hand-picked hex.
type Tone = 'bullish' | 'bearish' | 'win' | 'info';

function FilterChip({ label, active, onClick, tone }: { label: string; active: boolean; onClick: () => void; tone?: Tone }) {
  const toneColor = tone ? `var(--${tone})` : 'var(--accent)';
  const toneMuted = tone ? `var(--${tone}-muted)` : 'var(--accent-muted)';
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-100 shrink-0"
      style={{
        background: active ? toneMuted : 'var(--bg-card)',
        color: active ? toneColor : 'var(--text-secondary)',
        border: active ? `1px solid ${toneColor}` : '1px solid var(--border)',
      }}
    >
      {label}
    </button>
  );
}

export default function FeedFilterBar({ filters, onChange }: FeedFilterBarProps) {
  const { t } = useLang();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const hasActiveFilters =
    filters.market !== 'ALL' ||
    filters.timeframe !== 'ALL' ||
    filters.direction !== 'ALL' ||
    filters.status !== 'ALL' ||
    filters.sort !== 'newest';

  function clearAll() {
    onChange({ market: 'ALL', timeframe: 'ALL', direction: 'ALL', status: 'ALL', sort: 'newest', search: '' });
  }

  return (
    <div>
      {/* Toolbar — ticker search (desktop) + the Filter panel toggle. The market pill row that
          used to live here is gone (owner, 2026-09-04); markets are chips inside the panel. */}
      <div
        className="flex items-center gap-2 border-b py-2 pl-3"
        style={{ borderColor: 'var(--border)' }}
      >
        <IdeaTypeSwitch value={filters.ideaType} onChange={(ideaType) => onChange({ ideaType })} />

        {filters.market !== 'ALL' ? (
          <button
            onClick={() => onChange({ market: 'ALL' })}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1 text-xs font-bold whitespace-nowrap"
            style={{ borderRadius: 999, background: 'var(--text-primary)', color: 'var(--bg-base)' }}
            title={t('All Markets')}
          >
            {filters.market}
            <X size={11} />
          </button>
        ) : null}

        {/* Right: search + filter */}
        <div className="flex items-center gap-2 ml-auto pr-3 shrink-0">
          <div className="relative hidden sm:block">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => onChange({ search: e.target.value })}
              placeholder={t('Search ticker…')}
              className="input-base pl-8 pr-3 py-1.5 text-xs w-32 focus:w-44 transition-all duration-200"
            />
          </div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors duration-100"
            style={{
              borderRadius: 999,
              color: showAdvanced || hasActiveFilters ? 'var(--accent)' : 'var(--text-secondary)',
              background: showAdvanced || hasActiveFilters ? 'var(--accent-muted)' : 'transparent',
              border: showAdvanced || hasActiveFilters ? '1px solid var(--accent)' : '1px solid transparent',
            }}
          >
            <SlidersHorizontal size={13} />
            {t('Filter')}
            {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--accent)' }} />}
            <ChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms' }} />
          </button>
        </div>
      </div>

      {/* Mobile-only feed tabs — the sidebar hosting them on desktop doesn't exist below md,
          so the curated tabs stay reachable as a compact scrollable pill row. */}
      <div className="md:hidden flex items-center gap-1.5 px-3 py-2 border-b overflow-x-auto scrollbar-hide" style={{ borderColor: 'var(--border)' }}>
        {FEED_TAB_DEFS.map((tab) => {
          const isActive = filters.tab === tab.id;
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onChange({ tab: tab.id })}
              className="flex items-center gap-1 shrink-0 transition-all duration-100"
              style={{
                borderRadius: 999,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 700,
                background: isActive ? 'var(--text-primary)' : 'var(--bg-card)',
                color: isActive ? 'var(--bg-base)' : 'var(--text-secondary)',
                border: isActive ? '1px solid var(--text-primary)' : '1px solid var(--border)',
              }}
            >
              <TabIcon size={12} />
              {t(tab.label)}
            </button>
          );
        })}
      </div>

      {/* Mobile search */}
      <div className="sm:hidden px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={t('Search ticker, trader…')}
            className="input-base w-full pl-8 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="px-3 py-3 border-b animate-fade-in" style={{ borderColor: 'var(--border)', background: 'var(--bg-sunken)' }}>
          <div className="flex flex-col gap-3">
            <div>
              <p className="mb-2 uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Market')}</p>
              <div className="flex flex-wrap gap-1.5">
                {marketOptions.map((m) => (
                  <FilterChip key={m} label={m === 'ALL' ? t('All Markets') : m} active={filters.market === m} onClick={() => onChange({ market: m })} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="mb-2 uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Timeframe')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {timeframeOptions.map((tf) => (
                    <FilterChip key={tf} label={tf === 'ALL' ? t('All') : tf} active={filters.timeframe === tf} onClick={() => onChange({ timeframe: tf })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Direction')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {directionOptions.map((d) => (
                    <FilterChip key={d} label={d === 'ALL' ? t('All') : d} active={filters.direction === d} onClick={() => onChange({ direction: d })} tone={d === 'BUY' ? 'bullish' : d === 'SELL' ? 'bearish' : undefined} />
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filters.ideaType !== 'ANALYSIS' && (
              <div>
                <p className="mb-2 uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>Status</p>
                <div className="flex flex-wrap gap-1.5">
                  {statusOptions.map((s) => (
                    <FilterChip key={s} label={s === 'ALL' ? t('All') : s} active={filters.status === s} onClick={() => onChange({ status: s })} tone={s === 'WIN' ? 'bullish' : s === 'LOSS' ? 'bearish' : s === 'BE' ? 'win' : s === 'OPEN' ? 'info' : undefined} />
                  ))}
                </div>
              </div>
              )}
              <div>
                <p className="mb-2 uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>{t('Sort By')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {sortOptions.map((s) => (
                    <FilterChip key={s.value} label={t(s.label)} active={filters.sort === s.value} onClick={() => onChange({ sort: s.value })} />
                  ))}
                </div>
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearAll} className="flex items-center gap-1.5 text-xs font-semibold self-start transition-colors duration-100" style={{ color: 'var(--bearish)' }}>
                <X size={12} />
                {t('Clear all filters')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
